import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useClickOutside } from '../../lib/hooks/useOnClickOutside';
import SignOutIcon from './assets/sign-out.svg';
import HelpIcon from './assets/help.svg';
import BellIcon from './assets/bell.svg';
import { SettingsMenu } from './components/SettingsMenu';
import { NavLinks } from './components/NavLinks';
import { User } from './components/User';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { UserAvatar } from './components/UserAvatar';
import { SettingsButton } from './components/SettingsButton';
import { useNavigate } from 'react-router';
import { MobileTopBarMenu } from './components/MobileTopBarMenu';
import { Icon, IconType } from '../Props/Icon/Icon';
import { SwitchViewMenu, ViewOptions } from './components/SwitchViewMenu';
import { ThemeToggleButton } from './components/ThemeToggleButton';

export type Page = {
  name: React.ReactNode;
  path: string;
};

export type TopBarUIUser = {
  name: string | null;
  role: 'learner' | 'manager' | null;
  /**
   * this is a RAW base64 encoded binary blob with content type image/png, not a data URL
   */
  avatar: string | null;
};

export type TopBarUIProps = {
  pages: Page[];
  user: TopBarUIUser;
  logoUrl: string;
  onSignOut: () => void;
  managerView?: boolean;
};
export const TopBarUI: React.FC<TopBarUIProps> = ({ pages, user, logoUrl, onSignOut: onLogoff, managerView }) => {
  const [selectedMenu, setSelectedMenu] = useState<'user' | 'switchView'>();
  const handleHide = useCallback(() => {
    setSelectedMenu(undefined);
  }, []);
  const clickOutsideRef = useClickOutside<HTMLDivElement>(handleHide);
  const menuItems = useMemo(
    () => [
      {
        element: <SignOutIcon />,
        label: <NamedContentEntryText container={'span'} refKey="menuSystem" subKey="signOut" />,
        action: () => onLogoff(),
      },
    ],
    [onLogoff],
  );

  const showManageViewButton = useMemo(() => user.role === 'manager', [user.role]);
  const navigate = useNavigate();

  const onSwitchView = (view: ViewOptions) => {
    if (view === ViewOptions.MANAGER && !managerView) {
      navigate('manager');
    }

    if (view === ViewOptions.USER && managerView) {
      navigate('..');
    }

    handleHide();
  };

  return (
    <div ref={clickOutsideRef}>
      <TopBarOuter>
        <TopBarInner>
          <LeftSection>
            <Logo src={logoUrl} alt="Logo" />
            <DesktopNavGroup>
              {showManageViewButton && (
                <ManagerViewContainer>
                  <ViewChipButton
                    type="button"
                    $isManagerView={!!managerView}
                    onClick={() => setSelectedMenu((prev) => (prev === 'switchView' ? undefined : 'switchView'))}
                  >
                    <ManagerViewButtonContainer>
                      <ManagerViewButtonText>
                        <ManagerViewButtonHighlight>
                          <NamedContentEntryText
                            refKey="menuSystem"
                            container="span"
                            subKey={managerView ? 'managerView' : 'userView'}
                          />
                        </ManagerViewButtonHighlight>
                      </ManagerViewButtonText>
                      <Icon icon={IconType.CHEVRON_DOWN} width={14} height={14} />
                    </ManagerViewButtonContainer>
                  </ViewChipButton>
                  <SwitchViewMenu selectedMenu={selectedMenu} onSwitch={onSwitchView} isManagerView={managerView} />
                </ManagerViewContainer>
              )}
              <NavLinks pages={pages} isManagerView={managerView} />
            </DesktopNavGroup>
          </LeftSection>
          <MobileSeparator />
          <ResponsiveMenuSection>
            <ThemeToggleButton />
            <UtilityIconButton type="button" aria-label="Help">
              <HelpIcon />
            </UtilityIconButton>
            <UtilityIconButton type="button" aria-label="Notifications">
              <BellIcon />
            </UtilityIconButton>
            <User name={user.name} role={user.role} />
            <UserAvatar user={user} />
            <SettingsButton isOpen={selectedMenu === 'user'} setSelectedMenu={setSelectedMenu} />
            <SettingsMenu selectedMenu={selectedMenu} user={user} menuItems={menuItems} />
          </ResponsiveMenuSection>
          <ThemeToggleButton mobileOnly />
          <MobileTopBarMenu pages={pages} managerView={managerView} onSignOut={onLogoff} />
        </TopBarInner>
      </TopBarOuter>
    </div>
  );
};

const TopBarOuter = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  width: 100%;
  background: ${({ theme }) => theme.colors.prototype.card};
  border-bottom: 1px solid ${({ theme }) => theme.colors.prototype.border};
  backdrop-filter: blur(8px);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 3px;
  }
`;

const TopBarInner = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 60px;

  @media (max-width: 768px) {
    min-height: 58px;
    padding: 10px 12px;
    gap: 12px;
  }
`;

export const UserImg = styled.img`
  border-radius: 50%;
  width: 32px;
  height: 32px;
`;

const Logo = styled.img`
  max-height: 28px;
  max-width: 116px;
  object-fit: contain;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
`;

const DesktopNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileSeparator = styled.div`
  width: 1px;
  height: 32px;
  background: ${({ theme }) => theme.colors.topBarBorder};
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-left: auto;
  }
`;

const ManagerViewContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ManagerViewButtonContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;

  svg path {
    stroke: #fff;
  }
`;

const ViewChipButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$isManagerView',
})<{ $isManagerView: boolean }>`
  border: none;
  border-radius: 999px;
  background: ${({ theme, $isManagerView }) =>
    $isManagerView ? theme.colors.prototype.accent : theme.colors.prototype.userPrimary};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 1rem;
  letter-spacing: 0.5px;
  min-height: 30px;
  cursor: pointer;
  box-shadow: none;
  transition: filter 180ms ease;

  &:hover {
    filter: brightness(0.98);
  }
`;

const ManagerViewButtonText = styled.span`
  font-size: 12px;
  line-height: 18px;
`;

const ManagerViewButtonHighlight = styled.span`
  font-weight: 400;
`;

const UtilityIconButton = styled.button`
  width: 38px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
  cursor: pointer;
  transition:
    background-color 180ms ease,
    border-color 180ms ease,
    color 180ms ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.prototype.secondary};
    border-color: transparent;
    color: ${({ theme }) => theme.colors.prototype.foreground};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ResponsiveMenuSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;
